import useIsMounted from "ismounted";
import { useCallback, useEffect, useState } from "react";
import {
  FormState,
  useForm,
  UseFormHandleSubmit,
  UseFormRegister,
} from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Rule, RuleUpdateRequest } from "../../../../models/rule";
import { RuleService } from "../../../../services/rule-services";

export type EditRuleState = {
  rule: Rule | undefined;
  loading: boolean;
  saving: boolean;
  saveComplete: boolean;
  errorMessage: string;
  formState: FormState<RuleUpdateRequest>;
  register: UseFormRegister<RuleUpdateRequest>;
  handleSubmit: UseFormHandleSubmit<RuleUpdateRequest>;
  doSubmit: (data: RuleUpdateRequest) => Promise<void>;
  cancel: () => void;
  clearError: () => void;
};

type EditRuleProps = {
  ruleId: string | undefined;
};

export default function useEditRule() {
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const { ruleId } = useParams<EditRuleProps>();
  const [rule, setRule] = useState<Rule>();
  const [loading, setLoading] = useState<boolean>(true);
  const [saveComplete, setSaveComplete] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { register, setValue, handleSubmit, formState } =
    useForm<RuleUpdateRequest>({ reValidateMode: "onChange" });

  if (!ruleId) {
    navigate("/rules");
  }

  const doSubmit = async (data: RuleUpdateRequest) => {
    try {
      setSaving(true);
      setErrorMessage("");
      const updatedRule = await RuleService.updateRule(+ruleId!, data);
      if (updatedRule) {
        setSaveComplete(true);
      }
    } catch (e: any) {
      console.log(e);
      setErrorMessage(e.message ?? e);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setValue("name", rule!.name);
    setValue("description", rule!.description);
    setValue("ballotlabel", rule!.ballotlabel);
    setErrorMessage("");
    navigate(-1);
  };

  const clearError = () => {
    setErrorMessage("");
  };

  const getRule = useCallback(async () => {
    if (ruleId) {
      try {
        setLoading(true);
        setErrorMessage("");
        const newRule = await RuleService.getRule(+ruleId!);
        if (!newRule) {
          navigate("/rules");
        } else {
          setRule(newRule);
          setValue("name", newRule.name);
          setValue("description", newRule.description);
          setValue("ballotlabel", newRule.ballotlabel);
        }
      } catch (e: any) {
        console.log(e);
        setErrorMessage(e.message ?? e);
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleId, navigate]);

  useEffect(() => {
    getRule();
  }, [getRule]);

  useEffect(() => {
    if (saveComplete) {
      setTimeout(() => {
        navigate("/rules");
      }, 1500);
    }
  }, [saveComplete, navigate]);

  return {
    rule,
    loading,
    saveComplete,
    saving,
    errorMessage,
    formState,
    register,
    handleSubmit,
    doSubmit,
    cancel,
    clearError,
  };
}
